import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, ClipboardCheck, Users, Timer, Activity, ExternalLink, AlertTriangle,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";

const ATT_STATUS = {
  asiste: { label: "Asiste", cls: "bg-emerald-100 text-emerald-800" },
  retraso: { label: "Retraso", cls: "bg-amber-100 text-amber-800" },
  no_asiste: { label: "No asiste", cls: "bg-red-100 text-red-700" },
  enfermo: { label: "Enfermo", cls: "bg-orange-100 text-orange-800" },
  lesion: { label: "Lesión", cls: "bg-rose-100 text-rose-800" },
  motivos_personales: { label: "Motivos personales", cls: "bg-slate-100 text-slate-600" },
};
const CONV_STATUS = {
  titular: "Titular", suplente: "Suplente", convocado: "Convocado",
  no_convocado: "No convocado", lesion: "Lesión", no_disponible: "No disponible",
};
const SCOPES = [
  { v: "no_pre", label: "Total (sin pretemp.)" },
  { v: "competition", label: "Competición" },
  { v: "preseason", label: "Pretemporada" },
  { v: "all", label: "Todo" },
];

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1)
    .toLocaleDateString("es-ES", { month: "short", year: "numeric" });
};
const inScope = (phase, scope) =>
  scope === "all" ? true
    : scope === "preseason" ? phase === "PRESEASON"
      : scope === "competition" ? phase === "COMPETITION"
        : phase !== "PRESEASON"; // no_pre

const pct = (num, den) => (den ? Math.round((num / den) * 1000) / 10 : null);

const SummaryCard = ({ icon: Icon, label, value, sub, color = "text-purple-700", testId }) => (
  <Card className="border-slate-200" data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">
        <Icon className={`h-4 w-4 ${color}`} /> {label}
      </div>
      <div className="text-2xl font-extrabold text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

export default function PlayerStatDetail({ mode }) {
  const isAtt = mode === "attendance";
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const backTeamId = sp.get("team_id") || "";
  const backSeason = sp.get("season") || "";

  const [scope, setScope] = useState(sp.get("scope") || "no_pre");
  const [range, setRange] = useState({ from: "", to: "" });
  const [month, setMonth] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [metric, setMetric] = useState(sp.get("metric") === "minutes" ? "minutes" : sp.get("metric") === "participation" ? "participation" : "called");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const path = isAtt ? "attendance-detail" : "matches-detail";
    const params = {};
    if (range.from) params.date_from = range.from;
    if (range.to) params.date_to = range.to;
    api.get(`/players/${playerId}/${path}`, { params })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [playerId, isAtt, range.from, range.to]);

  const records = isAtt ? (data?.sessions || []) : (data?.matches || []);

  // Scope + month filtered record set (shared by summary, bars and list).
  const scoped = useMemo(
    () => records.filter((r) => inScope(r.season_phase, scope)),
    [records, scope],
  );
  const scopedTyped = useMemo(
    () => (isAtt ? scoped : scoped.filter((r) =>
      typeFilter === "all" ? true : typeFilter === "amistoso" ? r.is_friendly : !r.is_friendly)),
    [scoped, typeFilter, isAtt],
  );
  const monthRecords = useMemo(
    () => (month === "all" ? scopedTyped : scopedTyped.filter((r) => r.month === month)),
    [scopedTyped, month],
  );

  // Monthly bars (over scope+type set).
  const months = useMemo(() => {
    const map = new Map();
    scopedTyped.forEach((r) => {
      if (!map.has(r.month)) map.set(r.month, []);
      map.get(r.month).push(r);
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([m, recs]) => {
      const comp = recs.filter((r) => !r.pending);
      let value = null;
      if (isAtt) value = pct(comp.filter((r) => r.is_attended).length, comp.length);
      else if (metric === "called") value = pct(comp.filter((r) => r.called).length, comp.length);
      else if (metric === "participation") value = pct(comp.filter((r) => r.participated).length, comp.length);
      else value = pct(comp.reduce((a, r) => a + r.minutes, 0), comp.reduce((a, r) => a + r.duration, 0));
      return { month: m, label: monthLabel(m), value: value ?? 0, hasData: comp.length > 0 };
    });
  }, [scopedTyped, isAtt, metric]);

  const summary = useMemo(() => {
    const comp = monthRecords.filter((r) => !r.pending);
    const pending = monthRecords.filter((r) => r.pending).length;
    if (isAtt) {
      const attended = comp.filter((r) => r.is_attended).length;
      return { computable: comp.length, attended, pending, pct: pct(attended, comp.length) };
    }
    const called = comp.filter((r) => r.called).length;
    const participated = comp.filter((r) => r.participated).length;
    const minutes = comp.reduce((a, r) => a + r.minutes, 0);
    const available = comp.reduce((a, r) => a + r.duration, 0);
    return {
      played: comp.length, called, participated, minutes, available, pending,
      called_pct: pct(called, comp.length),
      participation_pct: pct(participated, comp.length),
      minutes_pct: pct(minutes, available),
    };
  }, [monthRecords, isAtt]);

  const listRecords = useMemo(() => {
    if (isAtt && statusFilter !== "all") {
      return monthRecords.filter((r) =>
        statusFilter === "pendiente" ? r.pending : r.status === statusFilter);
    }
    return monthRecords;
  }, [monthRecords, statusFilter, isAtt]);

  const goBack = () => navigate(
    `/players/${playerId}/dashboard?phase=${scope === "no_pre" || scope === "all" ? "" : scope === "competition" ? "COMPETITION" : "PRESEASON"}` +
    `${backTeamId ? `&team_id=${backTeamId}` : ""}${backSeason ? `&season=${backSeason}` : ""}`,
  );

  if (loading && !data) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-purple-600" /></div>;
  }
  if (!data) return <p className="text-sm text-slate-400 py-10 text-center">No se pudo cargar el detalle.</p>;

  const noData = isAtt ? summary.computable === 0 : summary.played === 0;

  return (
    <div className="space-y-5 pb-10" data-testid={`stat-detail-${mode}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={goBack} data-testid="stat-detail-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Volver
          </Button>
          <div>
            <h1 className="text-xl font-extrabold text-slate-900">
              {isAtt ? "Detalle de asistencia" : "Detalle de partidos"}
            </h1>
            <p className="text-xs text-slate-500">{data.player?.name} · {data.team?.name}</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3" data-testid="stat-detail-filters">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Periodo</label>
          <Select value={scope} onValueChange={(v) => { setScope(v); setMonth("all"); }}>
            <SelectTrigger className="w-52" data-testid="stat-detail-scope"><SelectValue /></SelectTrigger>
            <SelectContent>{SCOPES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Desde</label>
          <Input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="w-40" data-testid="stat-detail-from" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Hasta</label>
          <Input type="date" value={range.to} min={range.from || undefined} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="w-40" data-testid="stat-detail-to" />
        </div>
        {!isAtt && (
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Tipo</label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40" data-testid="stat-detail-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="competicion">Competición</SelectItem>
                <SelectItem value="amistoso">Amistosos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {month !== "all" && (
          <Button variant="ghost" className="text-purple-700 font-bold" onClick={() => setMonth("all")} data-testid="stat-detail-clear-month">
            ✕ {monthLabel(month)}
          </Button>
        )}
      </div>

      {summary.pending > 0 && (
        <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2" data-testid="stat-detail-provisional">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Porcentaje provisional: hay {summary.pending} {isAtt ? "sesión(es)" : "partido(s)"} pendiente(s) de registrar. No se cuentan como ausencia.
        </div>
      )}

      {noData ? (
        <Card className="border-slate-200"><CardContent className="py-12 text-center text-slate-400" data-testid="stat-detail-nodata">Sin datos</CardContent></Card>
      ) : (
        <>
          {/* Resumen */}
          {isAtt ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <SummaryCard icon={ClipboardCheck} label="% Asistencia" value={summary.pct == null ? "Sin datos" : `${summary.pct}%`} sub={`${summary.attended} asistencias / ${summary.computable} entrenamientos`} color="text-emerald-600" testId="summary-attendance" />
              <SummaryCard icon={Activity} label="Entrenos computables" value={summary.computable} sub={summary.pending ? `${summary.pending} pendientes` : "Todos registrados"} testId="summary-computable" />
              <SummaryCard icon={Users} label="Pendientes" value={summary.pending} sub="Sin registrar" color="text-amber-600" testId="summary-pending" />
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <SummaryCard icon={Users} label="% Convocatorias" value={summary.called_pct == null ? "Sin datos" : `${summary.called_pct}%`} sub={`${summary.called} / ${summary.played} partidos`} testId="summary-called" />
              <SummaryCard icon={Activity} label="% Participación" value={summary.participation_pct == null ? "Sin datos" : `${summary.participation_pct}%`} sub={`${summary.participated} con minutos`} color="text-emerald-600" testId="summary-participation" />
              <SummaryCard icon={Timer} label="% Minutos" value={summary.minutes_pct == null ? "Sin datos" : `${summary.minutes_pct}%`} sub={`${summary.minutes}' / ${summary.available}' disponibles`} color="text-blue-600" testId="summary-minutes" />
              <SummaryCard icon={ClipboardCheck} label="Partidos disputados" value={summary.played} sub={summary.pending ? `${summary.pending} pendientes` : "Computables"} color="text-slate-600" testId="summary-played" />
            </div>
          )}

          {/* Gráfico mensual */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-extrabold text-slate-800 flex items-center justify-between flex-wrap gap-2">
                <span>Evolución mensual{month !== "all" ? ` · ${monthLabel(month)}` : ""}</span>
                {!isAtt && (
                  <div className="flex gap-1">
                    {[["called", "Convoc."], ["participation", "Particip."], ["minutes", "Minutos"]].map(([k, l]) => (
                      <button key={k} onClick={() => setMetric(k)}
                        className={`text-[11px] font-bold px-2 py-1 rounded-full border ${metric === k ? "bg-purple-600 text-white border-purple-600" : "bg-white text-slate-500 border-slate-200"}`}
                        data-testid={`metric-${k}`}>{l}</button>
                    ))}
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {months.length ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={months} onClick={(e) => { if (e?.activeLabel) { const hit = months.find((m) => m.label === e.activeLabel); if (hit) setMonth(month === hit.month ? "all" : hit.month); } }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} cursor="pointer">
                      {months.map((m) => (
                        <Cell key={m.month} fill={m.month === month ? "#7E22CE" : (month === "all" ? "#A855F7" : "#CBD5E1")} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-slate-400 py-8 text-center">Sin datos mensuales.</p>}
              <p className="text-[11px] text-slate-400 text-center mt-1">Pulsa un mes para filtrar el resumen y el listado.</p>
            </CardContent>
          </Card>

          {/* Listado */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-extrabold text-slate-800 flex items-center justify-between flex-wrap gap-2">
                <span>{isAtt ? "Sesiones" : "Partidos"} ({listRecords.length})</span>
                {isAtt && (
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-44 h-8" data-testid="stat-detail-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los estados</SelectItem>
                      {Object.entries(ATT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      <SelectItem value="pendiente">Pendientes</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isAtt ? <AttendanceList rows={listRecords} /> : <MatchesList rows={listRecords} navigate={navigate} />}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function AttendanceList({ rows }) {
  if (!rows.length) return <p className="text-xs text-slate-400 py-6 text-center">Sin sesiones.</p>;
  return (
    <>
      {/* Desktop */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="attendance-table">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-2 pr-2">Fecha</th><th className="py-2 px-2">Equipo</th><th className="py-2 px-2">Sesión</th>
              <th className="py-2 px-2">Estado</th><th className="py-2 px-2">Observaciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.training_id} className="border-b last:border-0" data-testid={`att-row-${r.training_id}`}>
                <td className="py-2 pr-2 whitespace-nowrap">{r.date}</td>
                <td className="py-2 px-2">{r.team_name}</td>
                <td className="py-2 px-2 font-semibold text-slate-700">{r.name}</td>
                <td className="py-2 px-2">
                  {r.pending ? <Badge className="bg-amber-100 text-amber-800 border-0">Pendiente</Badge>
                    : <Badge className={`${ATT_STATUS[r.status]?.cls || "bg-slate-100 text-slate-600"} border-0`}>{ATT_STATUS[r.status]?.label || r.status}</Badge>}
                  {r.status === "retraso" && r.late_minutes ? <span className="ml-1 text-[11px] text-amber-700">+{r.late_minutes}'</span> : null}
                </td>
                <td className="py-2 px-2 text-slate-500">{r.late_reason || (r.notes ? r.notes.slice(0, 60) : "—")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {rows.map((r) => (
          <div key={r.training_id} className="border border-slate-200 rounded-lg p-3" data-testid={`att-card-${r.training_id}`}>
            <div className="flex items-center justify-between">
              <span className="font-bold text-slate-700 text-sm">{r.date}</span>
              {r.pending ? <Badge className="bg-amber-100 text-amber-800 border-0">Pendiente</Badge>
                : <Badge className={`${ATT_STATUS[r.status]?.cls || "bg-slate-100 text-slate-600"} border-0`}>{ATT_STATUS[r.status]?.label || r.status}{r.status === "retraso" && r.late_minutes ? ` +${r.late_minutes}'` : ""}</Badge>}
            </div>
            <div className="text-xs text-slate-500 mt-1">{r.team_name} · {r.name}</div>
            {(r.late_reason || r.notes) && <div className="text-xs text-slate-400 mt-1">{r.late_reason || r.notes.slice(0, 80)}</div>}
          </div>
        ))}
      </div>
    </>
  );
}

function MatchesList({ rows, navigate }) {
  if (!rows.length) return <p className="text-xs text-slate-400 py-6 text-center">Sin partidos.</p>;
  const open = (id) => navigate(`/matches/${id}`);
  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="matches-table">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-2 pr-2">Fecha</th><th className="py-2 px-2">Rival</th><th className="py-2 px-2">Tipo</th>
              <th className="py-2 px-2">Resultado</th><th className="py-2 px-2">Convocatoria</th>
              <th className="py-2 px-2">Participó</th><th className="py-2 px-2 text-right">Min</th><th className="py-2 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.match_id} className="border-b last:border-0" data-testid={`match-row-${r.match_id}`}>
                <td className="py-2 pr-2 whitespace-nowrap">{r.date}</td>
                <td className="py-2 px-2 font-semibold text-slate-700">{r.rival} {r.home_away === "casa" ? "(C)" : "(F)"}</td>
                <td className="py-2 px-2">{r.is_friendly ? "Amistoso" : r.match_type}</td>
                <td className="py-2 px-2">{r.result || "—"}</td>
                <td className="py-2 px-2">
                  {r.pending ? <Badge className="bg-amber-100 text-amber-800 border-0">Pendiente</Badge>
                    : <span className={r.called ? "text-slate-700" : "text-slate-400"}>{CONV_STATUS[r.status] || "—"}</span>}
                </td>
                <td className="py-2 px-2">{r.pending ? "—" : r.participated ? <span className="text-emerald-600 font-bold">Sí</span> : "No"}</td>
                <td className="py-2 px-2 text-right font-bold">{r.pending ? "—" : `${r.minutes}'/${r.duration}'`}</td>
                <td className="py-2 px-2 text-right">
                  <button onClick={() => open(r.match_id)} className="text-purple-600 hover:text-purple-800" title="Ver ficha" data-testid={`match-open-${r.match_id}`}><ExternalLink className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden space-y-2">
        {rows.map((r) => (
          <div key={r.match_id} className="border border-slate-200 rounded-lg p-3" data-testid={`match-card-${r.match_id}`} onClick={() => open(r.match_id)}>
            <div className="flex items-center justify-between">
              <span className="font-bold text-slate-700 text-sm">{r.rival} {r.home_away === "casa" ? "(C)" : "(F)"}</span>
              <span className="text-xs text-slate-400">{r.date}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">{r.is_friendly ? "Amistoso" : r.match_type} · {r.result || "sin resultado"}</div>
            <div className="text-xs mt-1 flex gap-3">
              {r.pending ? <span className="text-amber-700 font-semibold">Pendiente</span> : (
                <>
                  <span>{CONV_STATUS[r.status] || "—"}</span>
                  <span className={r.participated ? "text-emerald-600 font-bold" : "text-slate-400"}>{r.participated ? "Participó" : "No jugó"}</span>
                  <span className="font-bold">{r.minutes}'/{r.duration}'</span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
